import { ref, provide, inject, computed, type InjectionKey } from 'vue'

// 定义注入的key
const LoginDialogKey: InjectionKey<ReturnType<typeof createLoginDialog>> = Symbol('login-dialog')

type AuthMode = 'login' | 'register'

interface LoginDialogState {
  visible: boolean
  mode: AuthMode
  resolve: ((value: boolean) => void) | null
  reject: ((reason?: any) => void) | null
}

export function createLoginDialog() {
  const state = ref<LoginDialogState>({
    visible: false,
    mode: 'login',
    resolve: null,
    reject: null,
  })

  // 显示登录弹窗，返回Promise
  const showLoginDialog = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      state.value.mode = 'login'
      state.value.visible = true
      state.value.resolve = resolve
      state.value.reject = reject
    })
  }

  // 显示注册弹窗，返回Promise
  const showRegisterDialog = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      state.value.mode = 'register'
      state.value.visible = true
      state.value.resolve = resolve
      state.value.reject = reject
    })
  }

  // 关闭登录弹窗
  const closeLoginDialog = (success: boolean = false) => {
    state.value.visible = false
    if (state.value.resolve) {
      state.value.resolve(success)
      state.value.resolve = null
      state.value.reject = null
    }
  }

  // 登录成功
  const onLoginSuccess = () => {
    closeLoginDialog(true)
  }

  // 登录取消
  const onLoginCancel = () => {
    closeLoginDialog(false)
  }

  return {
    visible: computed({
      get: () => state.value.visible,
      set: (val: boolean) => {
        state.value.visible = val
        // 弹窗被外部关闭（如点击遮罩、ESC 等）时，resolve 为取消
        if (!val && state.value.resolve) {
          state.value.resolve(false)
          state.value.resolve = null
          state.value.reject = null
        }
      }
    }),
    mode: computed(() => state.value.mode),
    showLoginDialog,
    showRegisterDialog,
    closeLoginDialog,
    onLoginSuccess,
    onLoginCancel,
  }
}

// 提供登录弹窗方法
export function provideLoginDialog() {
  const dialog = createLoginDialog()
  provide(LoginDialogKey, dialog)
  return dialog
}

// 使用登录弹窗方法
export function useLoginDialog() {
  const dialog = inject(LoginDialogKey)
  if (!dialog) {
    throw new Error('useLoginDialog must be used after provideLoginDialog')
  }
  return dialog
}

// 全局登录弹窗状态（用于非组件内调用）
let globalLoginDialog: ReturnType<typeof createLoginDialog> | null = null

export function setGlobalLoginDialog(dialog: ReturnType<typeof createLoginDialog>) {
  globalLoginDialog = dialog
}

export function getGlobalLoginDialog() {
  return globalLoginDialog
}

// 全局显示登录弹窗
export async function showGlobalLoginDialog(): Promise<boolean> {
  if (globalLoginDialog) {
    return globalLoginDialog.showLoginDialog()
  }
  console.warn('Login dialog not initialized')
  return false
}

// 全局显示注册弹窗
export async function showGlobalRegisterDialog(): Promise<boolean> {
  if (globalLoginDialog) {
    return globalLoginDialog.showRegisterDialog()
  }
  console.warn('Login dialog not initialized')
  return false
}
